import { lazy } from 'react';

const isChunkLoadError = (error) =>
  error.name === 'ChunkLoadError' ||
  /Failed to fetch dynamically imported module|Loading (?:CSS )?chunk|Unable to preload CSS/.test(error.message ?? '');

const consumedUrlRetryMarkers = new Set();

const clearRetryFlag = (retryKey) => {
  let storageUnavailable = false;
  try {
    sessionStorage.removeItem(retryKey);
  } catch {
    storageUnavailable = true;
    /* sessionStorage may be blocked in a cross-origin iframe */
  }

  let urlMarkerPresent = false;
  try {
    const url = new URL(window.location.href);
    urlMarkerPresent = url.searchParams.has(retryKey);
    if (urlMarkerPresent) {
      url.searchParams.delete(retryKey);
      window.history.replaceState(window.history.state, '', url.toString());
    }
    consumedUrlRetryMarkers.delete(retryKey);
  } catch {
    if (storageUnavailable && urlMarkerPresent) {
      consumedUrlRetryMarkers.add(retryKey);
    }
    /* URL cleanup is best effort */
  }
};

/**
 * Retry wrapper for `React.lazy()` dynamic imports.
 *
 * Lifted verbatim (behaviour-for-behaviour) out of
 * `donation-elements/src/App.jsx` by CON-82 so every donor app's lazy
 * boundaries share one implementation. `donation-checkout` keeps its own
 * `src/utils/lazyWithRetry.js` for now; adopting this one is CON-83's option.
 *
 * WHY IT EXISTS (and why a bare `React.lazy` is not acceptable here):
 * chunk filenames are content-hashed, so every release invalidates them.
 * A donor holding stale HTML — cached by CloudFront or their own browser —
 * asks for a chunk URL that now 404s, React throws `ChunkLoadError`, and the
 * donation form is replaced by "Unable to load donation form". Retrying the
 * import once, and reloading the page so the browser picks up fresh HTML,
 * turns that into a recoverable blip.
 *
 * The retry is marked in `sessionStorage` when it is available and in a URL
 * search param when it is not. That fallback is not hypothetical: the
 * elements app runs inside a CROSS-ORIGIN IFRAME on a merchant's page, where
 * `sessionStorage` throws `SecurityError` under Safari's storage partitioning
 * and in private browsing. Without it, a blocked-storage donor would reload
 * forever.
 *
 * @param {() => Promise<{ default: React.ComponentType<any> }>} importFn
 *   Dynamic import function, e.g. `() => import('./EmbedForm')`
 * @param {string} chunkName Human-readable name, used for the retry flag and logs
 * @returns {React.LazyExoticComponent<any>} Lazy component with retry logic
 */
export const lazyWithRetry = (importFn, chunkName) =>
  lazy(() => importWithRetry(importFn, chunkName));

/**
 * Imports one lazy module with the shared stale-chunk recovery behavior.
 *
 * @param {() => Promise<{ default: React.ComponentType<any> }>} importFn
 * @param {string} chunkName
 * @returns {Promise<{ default: React.ComponentType<any> }>} Imported module
 */
export function importWithRetry(importFn, chunkName) {
  const retryKey = `chunk_retry_${chunkName}`;

  return importFn()
    .then((module) => {
      clearRetryFlag(retryKey);
      return module;
    })
    .catch((error) => {
      // Only retry on chunk load failures, not other import errors
      if (!isChunkLoadError(error)) {
        throw error; // Not a chunk error — don't retry, let it propagate
      }

      // Detect if we already retried, using two fallback mechanisms:
      // 1. sessionStorage (preferred) — works same-origin
      // 2. URL search param (fallback) — works in cross-origin iframes where
      //    sessionStorage throws SecurityError (Safari, private browsing, etc.)
      let alreadyRetried = false;
      try {
        alreadyRetried = sessionStorage.getItem(retryKey) === '1';
      } catch {
        // sessionStorage blocked — check URL param fallback
        const url = new URL(window.location.href);
        alreadyRetried = url.searchParams.get(retryKey) === '1';
        if (alreadyRetried && consumedUrlRetryMarkers.delete(retryKey)) {
          alreadyRetried = false;
        }
      }

      if (alreadyRetried) {
        // Already retried once — clear the flag and let the error propagate
        clearRetryFlag(retryKey);
        console.error(`[lazyWithRetry] Chunk "${chunkName}" failed after retry:`, error);
        throw error;
      }

      // Mark that we're retrying, then reload the page to bust stale cache
      console.warn(`[lazyWithRetry] Chunk "${chunkName}" failed to load. Retrying...`, error);
      try {
        sessionStorage.setItem(retryKey, '1');
      } catch {
        // sessionStorage blocked — append retry flag to URL so we can detect it after reload
        const url = new URL(window.location.href);
        url.searchParams.set(retryKey, '1');
        window.location.href = url.toString();
        return new Promise(() => {});
      }
      window.location.reload();

      // Return a never-resolving promise to prevent React from rendering
      // while the page reloads (avoids a flash of error UI before reload completes)
      return new Promise(() => {});
    });
}

export default lazyWithRetry;
